import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  CircularProgress, Stack, MenuItem, IconButton, Divider, Grid, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useParams } from 'react-router-dom';
import {
  inventoryApiService,
  StockTransfer,
  Warehouse,
  Product,
} from '../../services/inventoryService';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PillTone, useConfirm } from '../../components/ui';
import { tokens } from '../../theme';
import { countLabel, formatDateTime, formatNumber, toNumber } from '../../utils/format';

interface TransferItem { productId: string; requestedQty: string }

type ApiIssue = { field?: string; message: string };

const unitLabel = (unit?: string) => (unit === 'szt' ? 'szt.' : unit || '');

/** Ilosc bez zbednych zer: 5 → „5”, 0.125 → „0,125”. */
const formatQty = (value: unknown) => {
  const n = Math.round(toNumber(value) * 1000) / 1000;
  const decimals = Number.isInteger(n) ? 0 : (String(n).split('.')[1] || '').length;
  return formatNumber(n, decimals);
};

const STATUS: Record<string, { label: string; tone: PillTone }> = {
  PENDING: { label: 'Oczekuje na wydanie', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzone', tone: 'info' },
  COMPLETED: { label: 'Zrealizowane', tone: 'success' },
  CANCELLED: { label: 'Anulowane', tone: 'danger' },
};

// Pola z API, ktore da sie pokazac przy polu w oknie; pozostale trafiaja do belki z etykieta.
const FORM_FIELDS = ['fromWarehouseId', 'toWarehouseId', 'notes'];
const issueLabel = (field?: string) => {
  if (!field) return '';
  if (field === 'fromWarehouseId') return 'Z magazynu: ';
  if (field === 'toWarehouseId') return 'Do magazynu: ';
  if (field === 'notes') return 'Uwagi: ';
  if (field === 'items') return 'Produkty: ';
  if (/requestedQty$/.test(field)) return 'Ilość: ';
  if (/productId$/.test(field)) return 'Produkt: ';
  return '';
};

export default function StockTransfersPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const confirm = useConfirm();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<ApiIssue[]>([]);
  const [saving, setSaving] = useState(false);

  const [createDialog, setCreateDialog] = useState(false);
  const [fromWarehouseId, setFromWarehouseId] = useState('');
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [items, setItems] = useState<TransferItem[]>([{ productId: '', requestedQty: '' }]);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      setLoading(true);
      const [whs, prods] = await Promise.all([
        inventoryApiService.getWarehousesForBrand(brandId),
        inventoryApiService.getProducts({ brandId }),
      ]);
      setWarehouses(whs);
      setProducts(prods);
      // Przesuniecia zbieramy ze wszystkich magazynow marki
      const allTransfers: StockTransfer[] = [];
      for (const wh of whs) {
        const t = await inventoryApiService.getTransfers({ fromWarehouseId: wh.id });
        allTransfers.push(...t);
      }
      const unique = Array.from(new Map(allTransfers.map(t => [t.id, t])).values());
      setTransfers(unique.sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()));
    } catch {
      setError('Nie udało się wczytać przesunięć magazynowych. Odśwież stronę.');
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  const clearErrors = () => { setError(''); setErrorDetails([]); };

  const openCreate = () => {
    setFromWarehouseId('');
    setToWarehouseId('');
    setTransferNotes('');
    setItems([{ productId: '', requestedQty: '' }]);
    clearErrors();
    setCreateDialog(true);
  };

  const addItem = () => setItems(i => [...i, { productId: '', requestedQty: '' }]);
  const removeItem = (idx: number) => setItems(i => i.filter((_, j) => j !== idx));
  const updateItem = (idx: number, field: keyof TransferItem, value: string) => {
    setItems(i => i.map((item, j) => j === idx ? { ...item, [field]: value } : item));
  };

  const createTransfer = async () => {
    if (!fromWarehouseId || !toWarehouseId) return;
    setSaving(true);
    try {
      await inventoryApiService.createTransfer({
        fromWarehouseId,
        toWarehouseId,
        notes: transferNotes,
        items: items.filter(i => i.productId && i.requestedQty).map(i => ({
          productId: i.productId,
          requestedQty: parseFloat(i.requestedQty),
        })),
      });
      setCreateDialog(false);
      clearErrors();
      load();
    } catch (e: any) {
      const details: ApiIssue[] = Array.isArray(e?.response?.data?.details) ? e.response.data.details : [];
      setErrorDetails(details);
      setError(details.length
        ? 'Nie udało się utworzyć przesunięcia — sprawdź dane w formularzu.'
        : e?.response?.data?.error || 'Nie udało się utworzyć przesunięcia. Spróbuj ponownie.');
    } finally {
      setSaving(false);
    }
  };

  const transferDetails = (t?: StockTransfer) => (t ? (
    <>
      <strong>{t.fromWarehouse.name} → {t.toWarehouse.name}</strong>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25, color: tokens.inkSoft, fontSize: '0.875rem' }}>
        {t.items.slice(0, 5).map(item => (
          <li key={item.id}>
            {item.product.name} — <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatQty(item.requestedQty)} {unitLabel(item.product.unit)}</Box>
          </li>
        ))}
        {t.items.length > 5 && <li>i {countLabel(t.items.length - 5, 'kolejna pozycja', 'kolejne pozycje', 'kolejnych pozycji')}</li>}
      </Box>
    </>
  ) : undefined);

  const complete = async (id: string) => {
    const t = transfers.find(x => x.id === id);
    const ok = await confirm({
      title: 'Wydać towar?',
      details: transferDetails(t),
      description: t
        ? `Stany magazynowe zostaną zaktualizowane: towar zejdzie ze stanu magazynu „${t.fromWarehouse.name}” i zostanie dodany do „${t.toWarehouse.name}”.`
        : 'Stany magazynowe zostaną zaktualizowane.',
      confirmLabel: 'Wydaj towar',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      await inventoryApiService.completeTransfer(id);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Nie udało się wydać towaru. Spróbuj ponownie.');
    }
  };

  const cancel = async (id: string) => {
    const t = transfers.find(x => x.id === id);
    const ok = await confirm({
      title: 'Anulować przesunięcie?',
      details: transferDetails(t),
      description: 'Towar nie zostanie wydany, a stany obu magazynów pozostaną bez zmian.',
      confirmLabel: 'Anuluj przesunięcie',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await inventoryApiService.cancelTransfer(id);
      load();
    } catch {
      setError('Nie udało się anulować przesunięcia. Spróbuj ponownie.');
    }
  };

  const fieldError = (field: string) => (createDialog ? errorDetails.find(d => d.field === field)?.message : undefined);

  const renderError = (inDialog: boolean) => {
    if (!error) return null;
    const list = inDialog ? errorDetails.filter(d => !d.field || !FORM_FIELDS.includes(d.field)) : errorDetails;
    return (
      <Alert severity="error" onClose={clearErrors} sx={{ mb: 2 }}>
        <Box>{error}</Box>
        {list.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {list.map((item, index) => (
              <li key={`${item.field || 'field'}-${index}`}>{issueLabel(item.field)}{item.message}</li>
            ))}
          </Box>
        )}
      </Alert>
    );
  };

  const pendingCount = transfers.filter(t => t.status === 'PENDING').length;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Usługi i magazyn' }, { label: 'Marki i oddziały', to: '/salon/brands' }]}
        title="Przesunięcia magazynowe"
        description={pendingCount > 0
          ? `${countLabel(pendingCount, 'przesunięcie czeka', 'przesunięcia czekają', 'przesunięć czeka')} na wydanie towaru.`
          : 'Rozdzielaj produkty między magazynem głównym a oddziałami marki.'}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Nowe przesunięcie
          </Button>
        }
      />

      {!createDialog && renderError(false)}

      {loading ? (
        <LoadingState variant="cards" count={2} />
      ) : transfers.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<SwapHorizIcon />}
            title="Brak przesunięć magazynowych"
            description="Utwórz przesunięcie, aby przekazać produkty z jednego magazynu marki do drugiego."
            actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>Nowe przesunięcie</Button>}
          />
        </SectionCard>
      ) : (
        <Stack spacing={2}>
          {transfers.map(t => {
            const st = STATUS[t.status] || { label: t.status, tone: 'neutral' as PillTone };
            return (
              <Paper key={t.id} sx={{ overflow: 'hidden' }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                  gap={1.5}
                  sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 1.75, md: 2 }, pb: 1.5 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                      <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{t.fromWarehouse.name}</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 18, color: tokens.muted }} titleAccess="do" />
                      <Typography sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{t.toWarehouse.name}</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.75 }}>
                      <StatusPill label={st.label} tone={st.tone} />
                      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        Utworzone {formatDateTime(t.requestedAt)}
                        {t.completedAt ? ` · wydane ${formatDateTime(t.completedAt)}` : ''}
                      </Typography>
                    </Stack>
                    {t.notes && <Typography variant="body2" sx={{ color: tokens.inkSoft, mt: 0.75 }}>{t.notes}</Typography>}
                  </Box>
                  {t.status === 'PENDING' && (
                    <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
                      <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => complete(t.id)}>
                        Wydaj towar
                      </Button>
                      <Button size="small" variant="text" color="error" onClick={() => cancel(t.id)}>
                        Anuluj
                      </Button>
                    </Stack>
                  )}
                </Stack>
                <Divider />
                <Stack divider={<Divider />}>
                  {t.items.map(item => (
                    <Stack
                      key={item.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="baseline"
                      gap={2}
                      sx={{ px: { xs: 2, md: 2.5 }, py: 1.25 }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '0.90625rem', minWidth: 0, wordBreak: 'break-word' }}>
                        {item.product.name}
                      </Typography>
                      <Box sx={{ textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.90625rem' }}>
                          {formatQty(item.requestedQty)} {unitLabel(item.product.unit)}
                        </Typography>
                        {item.sentQty !== null && item.sentQty !== undefined && (
                          <Typography variant="caption" component="div">
                            wydano {formatQty(item.sentQty)} {unitLabel(item.product.unit)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}

      <Dialog open={createDialog} onClose={() => { setCreateDialog(false); clearErrors(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Nowe przesunięcie magazynowe</DialogTitle>
        <DialogContent>
          {createDialog && renderError(true)}
          {warehouses.length < 2 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Do przesunięcia potrzebne są co najmniej dwa magazyny. Dodaj kolejny oddział do marki.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Z magazynu" value={fromWarehouseId}
                onChange={e => setFromWarehouseId(e.target.value)}
                error={!!fieldError('fromWarehouseId')} helperText={fieldError('fromWarehouseId')}
              >
                {warehouses.map(w => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}{w.type === 'CENTRAL' ? ' · magazyn główny' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Do magazynu" value={toWarehouseId}
                onChange={e => setToWarehouseId(e.target.value)}
                error={!!fieldError('toWarehouseId')} helperText={fieldError('toWarehouseId')}
              >
                {warehouses.filter(w => w.id !== fromWarehouseId).map(w => (
                  <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Uwagi (opcjonalnie)" value={transferNotes}
                onChange={e => setTransferNotes(e.target.value)}
                error={!!fieldError('notes')} helperText={fieldError('notes')}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2.5 }} />
          <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.25 }}>Produkty do przesunięcia</Typography>
          <Typography variant="caption" component="div" sx={{ mb: 1.5 }}>
            Pozycje bez wybranego produktu lub ilości zostaną pominięte.
          </Typography>

          <Stack spacing={1.5}>
            {items.map((item, idx) => {
              const unit = unitLabel(products.find(p => p.id === item.productId)?.unit);
              return (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: items.length > 1 ? 'minmax(0, 1fr) 112px 40px' : 'minmax(0, 1fr) 112px',
                    gap: 1,
                    alignItems: 'end',
                  }}
                >
                  <TextField
                    select fullWidth label={idx === 0 ? 'Produkt' : undefined} value={item.productId}
                    onChange={e => updateItem(idx, 'productId', e.target.value)}
                    SelectProps={{ SelectDisplayProps: { 'aria-label': `Produkt ${idx + 1}` } as React.HTMLAttributes<HTMLDivElement> }}
                  >
                    {products.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({unitLabel(p.unit)})</MenuItem>)}
                  </TextField>
                  <TextField
                    label={idx === 0 ? 'Ilość' : undefined} type="number" value={item.requestedQty}
                    onChange={e => updateItem(idx, 'requestedQty', e.target.value)}
                    inputProps={{ min: 0.001, step: 0.001, 'aria-label': `Ilość ${idx + 1}${unit ? ` (${unit})` : ''}` }}
                  />
                  {items.length > 1 && (
                    <Tooltip title="Usuń pozycję">
                      <IconButton aria-label={`Usuń pozycję ${idx + 1}`} onClick={() => removeItem(idx)} sx={{ mb: 0.5 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Stack>
          <Button size="small" variant="text" startIcon={<AddIcon />} onClick={addItem} sx={{ mt: 1.5 }}>
            Dodaj kolejny produkt
          </Button>
          {products.length === 0 && (
            <Typography variant="caption" component="div" sx={{ mt: 1 }}>
              Marka nie ma jeszcze własnych produktów do przesunięcia.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => { setCreateDialog(false); clearErrors(); }}>Anuluj</Button>
          <Button
            variant="contained" onClick={createTransfer}
            disabled={saving || !fromWarehouseId || !toWarehouseId || items.every(i => !i.productId)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Utwórz przesunięcie
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
